import { IRoute } from '@numeo/types-core'
import { Card, FormControl, FormLabel, Input, Select, Stack, Button, Option, Divider, Typography } from '@mui/joy'
import DeleteIcon from '@mui/icons-material/Delete'
import React from 'react'

interface RouteStopEditorProps {
    stop: IRoute
    index: number
    onChange: (stop: IRoute) => void
    onRemove: () => void
    disabled?: boolean
}

export const RouteStopEditor: React.FC<RouteStopEditorProps> = ({ stop, index, onChange, onRemove, disabled = false }) => {
    const handleChange = (field: string, value: string | number | null) => {
        if (field.startsWith('location.')) {
            const locationField = field.split('.')[1]
            onChange({
                ...stop,
                location: {
                    ...stop.location,
                    [locationField]: value || null,
                },
            })
        } else {
            onChange({
                ...stop,
                [field]: value || null,
            })
        }
    }

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="title-sm">Stop {index + 1}</Typography>
                    <Button size="sm" variant="outlined" color="danger" onClick={onRemove} disabled={disabled} startDecorator={<DeleteIcon />}>
                        Remove
                    </Button>
                </Stack>

                <FormControl>
                    <FormLabel>Stop Type</FormLabel>
                    <Select value={stop.type} onChange={(_, value) => handleChange('type', value)} disabled={disabled} size="sm">
                        <Option value="pickup">Pickup</Option>
                        <Option value="delivery">Delivery</Option>
                    </Select>
                </FormControl>

                <Divider>Location</Divider>

                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ flex: 2 }}>
                        <FormLabel>City</FormLabel>
                        <Input
                            placeholder="City"
                            value={stop.location.city || ''}
                            onChange={(e) => handleChange('location.city', e.target.value)}
                            disabled={disabled}
                            size="sm"
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>State</FormLabel>
                        <Input
                            placeholder="State"
                            value={stop.location.state || ''}
                            onChange={(e) => handleChange('location.state', e.target.value)}
                            disabled={disabled}
                            size="sm"
                        />
                    </FormControl>
                </Stack>

                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>ZIP Code</FormLabel>
                        <Input
                            placeholder="ZIP"
                            value={stop.location.zip || ''}
                            onChange={(e) => handleChange('location.zip', e.target.value)}
                            disabled={disabled}
                            size="sm"
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Country</FormLabel>
                        <Input
                            placeholder="Country"
                            value={stop.location.country || ''}
                            onChange={(e) => handleChange('location.country', e.target.value)}
                            disabled={disabled}
                            size="sm"
                        />
                    </FormControl>
                </Stack>

                <FormControl>
                    <FormLabel>Street Address</FormLabel>
                    <Input
                        placeholder="Street"
                        value={stop.location.street || ''}
                        onChange={(e) => handleChange('location.street', e.target.value)}
                        disabled={disabled}
                        size="sm"
                    />
                </FormControl>

                <Divider>Schedule</Divider>

                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Date</FormLabel>
                        <Input type="date" value={stop.date || ''} onChange={(e) => handleChange('date', e.target.value)} disabled={disabled} size="sm" />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Time</FormLabel>
                        <Input type="time" value={stop.time || ''} onChange={(e) => handleChange('time', e.target.value)} disabled={disabled} size="sm" />
                    </FormControl>
                </Stack>

                <Divider>Additional Details</Divider>

                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Facility</FormLabel>
                        <Input
                            placeholder="Facility Name"
                            value={stop.facility || ''}
                            onChange={(e) => handleChange('facility', e.target.value)}
                            disabled={disabled}
                            size="sm"
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Facility Code</FormLabel>
                        <Input
                            placeholder="Code"
                            value={stop.facilityCode || ''}
                            onChange={(e) => handleChange('facilityCode', e.target.value)}
                            disabled={disabled}
                            size="sm"
                        />
                    </FormControl>
                </Stack>

                <FormControl>
                    <FormLabel>Appointment Number</FormLabel>
                    <Input
                        placeholder="Appointment #"
                        value={stop.appointmentNumber || ''}
                        onChange={(e) => handleChange('appointmentNumber', e.target.value)}
                        disabled={disabled}
                        size="sm"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Distance (miles)</FormLabel>
                    <Input
                        type="number"
                        placeholder="Distance"
                        value={stop.distance || ''}
                        onChange={(e) => handleChange('distance', parseFloat(e.target.value) || 0)}
                        disabled={disabled}
                        size="sm"
                    />
                </FormControl>
            </Stack>
        </Card>
    )
}
